import { useState } from 'react';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';
import ExistingSupplierPicker from './ExistingSupplierPicker';
import { findSupplierByTaxId } from './existingSupplier';
import { useTranslation } from 'react-i18next';

import useFundContext from '@/contexts/Fund/useFundContext';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { ActivityDomainDtoIn } from '@/models/in/ActivityDomainDtoIn';
import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { SupplierDtoIn } from '@/models/in/SupplierDtoIn';
import { NewProjectSupplierDtoOut } from '@/models/out/ProjectDtoOut';
import { OTHER_OPTION_ID } from '@/pages/newProject/schemas/projectFormSchema';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import Button from '@/components/ui/Button';

/**
 * "הקמת ספק" — adds a supplier to an existing project of the fund, for suppliers that come up
 * after the project was created. A brand-new supplier enters the admin suppliers approval queue;
 * an existing one (matched by ח.פ) is only linked to the project.
 */
const NewSupplierPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();

  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [activityDomainId, setActivityDomainId] = useState<number | null>(null);
  const [activityDomainOther, setActivityDomainOther] = useState('');
  const [projectId, setProjectId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { data: projects, isLoading: isLoadingProjects } = useGetAuthApi<ProjectDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/projects` : ''
  );
  const { data: activityDomains } = useGetAuthApi<ActivityDomainDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/activity-domains` : ''
  );

  const { mutateAsync: createSupplier, isPending } = usePostAuthApi<SupplierDtoIn, NewProjectSupplierDtoOut>(
    currentFund?.id ? `funds/${currentFund.id}/projects/suppliers` : ''
  );

  const isOtherDomain = activityDomainId === OTHER_OPTION_ID;

  // The suppliers the fund already works with (SPR-10302): pick one, or type its tax id, and the
  // rest of the form fills in. It still goes to the office - a supplier is approved per project.
  const { data: fundSuppliers } = useGetAuthApi<SupplierFullDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/suppliers` : ''
  );

  const fillFromSupplier = (supplier: SupplierFullDtoIn) => {
    setName(supplier.name);
    setTaxId(supplier.taxId);
    setActivityDomainId(supplier.activityDomainId ?? (supplier.activityDomainOther ? OTHER_OPTION_ID : null));
    setActivityDomainOther(supplier.activityDomainOther ?? '');
    setError(null);
  };

  const resetForm = () => {
    setName('');
    setTaxId('');
    setActivityDomainId(null);
    setActivityDomainOther('');
    setProjectId(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('projects.wizard.validation.supplierNameRequired'));
      return;
    }
    if (!taxId.trim()) {
      setError(t('projects.wizard.validation.supplierTaxIdRequired'));
      return;
    }
    if (activityDomainId === null) {
      setError(t('projects.wizard.validation.activityDomainRequired'));
      return;
    }
    if (isOtherDomain && !activityDomainOther.trim()) {
      setError(t('projects.wizard.validation.activityDomainOtherRequired'));
      return;
    }
    if (projectId === null) {
      setError(t('projects.newSupplier.projectPlaceholder'));
      return;
    }

    setError(null);
    try {
      await createSupplier({
        name: name.trim(),
        taxId: taxId.trim(),
        activityDomainId: isOtherDomain ? null : activityDomainId,
        activityDomainOther: isOtherDomain ? activityDomainOther.trim() : null,
        projectId,
      });
      setIsSuccess(true);
    } catch (err) {
      console.error('Error creating supplier:', err);
      setError(t('projects.newSupplier.errorCreating'));
    }
  };

  if (isSuccess) {
    return (
      <SuccessScreen
        title={t('projects.newSupplier.successTitle')}
        description={t('projects.newSupplier.successSubtitle')}
        secondaryButtonText={t('projects.newSupplier.addAnother')}
        secondaryButtonAction={() => {
          resetForm();
          setIsSuccess(false);
        }}
      />
    );
  }

  const hasProjects = (projects?.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-6 max-w-[760px]">
      <div className="flex flex-col gap-1">
        <h2 className="heading-h3-semibold text-(--font-primary)">{t('projects.newSupplier.title')}</h2>
        <p className="body-md-regular text-(--font-secondary)">{t('projects.newSupplier.description')}</p>
      </div>

      {!isLoadingProjects && !hasProjects ? (
        <p className="body-md-regular text-(--font-secondary)">{t('projects.newSupplier.noProjects')}</p>
      ) : (
        <>
          <ExistingSupplierPicker suppliers={fundSuppliers ?? []} onPick={fillFromSupplier} />
          <div className="grid grid-cols-2 max-md:grid-cols-1 gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="new-supplier-name" className="body-sm-semibold text-(--font-secondary)">
                {t('projects.newSupplier.nameLabel')}
              </label>
              <input
                id="new-supplier-name"
                type="text"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('projects.newSupplier.namePlaceholder')}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="new-supplier-taxid" className="body-sm-semibold text-(--font-secondary)">
                {t('projects.newSupplier.taxIdLabel')}
              </label>
              <input
                id="new-supplier-taxid"
                type="text"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={taxId}
                onChange={(e) => {
                  const known = findSupplierByTaxId(fundSuppliers, e.target.value);
                  if (known) {
                    fillFromSupplier(known);
                  } else {
                    setTaxId(e.target.value);
                    if (error) setError(null);
                  }
                }}
                placeholder={t('projects.newSupplier.taxIdPlaceholder')}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="new-supplier-domain" className="body-sm-semibold text-(--font-secondary)">
                {t('projects.newSupplier.activityDomainLabel')}
              </label>
              <select
                id="new-supplier-domain"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={activityDomainId ?? ''}
                onChange={(e) => {
                  setActivityDomainId(e.target.value === '' ? null : Number(e.target.value));
                  if (error) setError(null);
                }}
              >
                <option value="">{t('projects.newSupplier.activityDomainPlaceholder')}</option>
                {(activityDomains ?? []).map((domain) => (
                  <option key={domain.id} value={domain.id}>{domain.name}</option>
                ))}
                <option value={OTHER_OPTION_ID}>{t('projects.newSupplier.activityDomainOther')}</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="new-supplier-project" className="body-sm-semibold text-(--font-secondary)">
                {t('projects.newSupplier.projectLabel')}
              </label>
              <select
                id="new-supplier-project"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={projectId ?? ''}
                onChange={(e) => {
                  setProjectId(e.target.value === '' ? null : Number(e.target.value));
                  if (error) setError(null);
                }}
              >
                <option value="">{t('projects.newSupplier.projectPlaceholder')}</option>
                {(projects ?? []).map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>

          {isOtherDomain && (
            <div className="flex flex-col gap-1">
              <input
                type="text"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={activityDomainOther}
                onChange={(e) => {
                  setActivityDomainOther(e.target.value);
                  if (error) setError(null);
                }}
                placeholder={t('projects.newSupplier.activityDomainOtherPlaceholder')}
              />
            </div>
          )}

          {error && <p className="text-red-500 body-sm-regular">{error}</p>}

          <div>
            <Button variant="primary" onClick={handleSubmit} disabled={isPending}>
              {isPending ? t('saving') : t('projects.newSupplier.submit')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default NewSupplierPage;
